import { OrderTableData, UserRole } from '../../../OrdersPage/types';
import {
  COLLECT_EDITABLE_STATUSES,
  COLLECT_NETWORK_TAG_ID,
  COLLECT_SOURCE_TAG_ID,
  PARTNER_SOURCES,
} from './constants';
import {
  AgreementDecision,
  AgreementDecisions,
  CollectCounts,
  CollectProductData,
  CollectStatus,
  CollectTab,
  ProductExtraOptions,
  ProductExtras,
  ProductMark,
  ProductMarks,
} from './types';

// Включён ли тег нового UI на аптеке (source) или на сети (network)
const hasCollectTag = (data: OrderTableData): boolean =>
  Boolean(
    data?.sourceTags?.some((tag) => tag.id === COLLECT_SOURCE_TAG_ID) ||
      data?.networkTags?.some((tag) => tag.id === COLLECT_NETWORK_TAG_ID)
  );

// Новый UI сборки заказа включён только для фармацевта/управляющего
// на заказах Glovo/Wolt И при наличии тега на аптеке или сети
export const isCollectUiEnabled = (
  userRole: string,
  data: OrderTableData
): boolean =>
  [UserRole.PHARMACIST, UserRole.PHARMACY_MANAGER].includes(
    userRole as UserRole
  ) &&
  PARTNER_SOURCES.includes(data?.userSource || '') &&
  hasCollectTag(data);

export const isCollectEditable = (data: OrderTableData): boolean =>
  COLLECT_EDITABLE_STATUSES.includes(data.statusPharmacy) &&
  (data.userSource === 'glovo_user' ||
    (data.userSource === 'wolt_user' && !data.isPartnerOrderUpdated));

export const isProblemStatus = (status: CollectStatus): boolean =>
  status !== CollectStatus.IN_STOCK;

export const getProductImageUrl = (sku: string): string =>
  `https://db-images.object.pscloud.io/optimized_v4_img_small_${
    sku?.includes('_discount') ? sku.split('_')[0] : sku
  }.webp`;

export const getCollectCounts = (
  products: CollectProductData[],
  marks: ProductMarks
): CollectCounts => {
  const done = products.filter((product) => marks[product.sku]).length;
  const problems = products.filter(
    (product) =>
      marks[product.sku] && isProblemStatus(marks[product.sku].status)
  ).length;

  return {
    all: products.length,
    pending: products.length - done,
    done,
    problems,
  };
};

export const filterProductsByTab = (
  products: CollectProductData[],
  marks: ProductMarks,
  tab: CollectTab
): CollectProductData[] => {
  switch (tab) {
    case CollectTab.PENDING:
      return products.filter((product) => !marks[product.sku]);
    case CollectTab.DONE:
      return products.filter((product) => marks[product.sku]);
    case CollectTab.PROBLEMS:
      return products.filter(
        (product) =>
          marks[product.sku] && isProblemStatus(marks[product.sku].status)
      );
    default:
      return products;
  }
};

// Количество товаров по каждому статусу (для чипов-счётчиков)
export const getStatusCounts = (
  products: CollectProductData[],
  marks: ProductMarks
): Record<CollectStatus, number> => {
  const result = Object.values(CollectStatus).reduce(
    (acc, status) => ({ ...acc, [status]: 0 }),
    {} as Record<CollectStatus, number>
  );
  products.forEach((product) => {
    const mark = marks[product.sku];
    if (mark) result[mark.status] += 1;
  });
  return result;
};

export const getProblemProducts = (
  products: CollectProductData[],
  marks: ProductMarks
): CollectProductData[] =>
  products.filter(
    (product) =>
      marks[product.sku] && isProblemStatus(marks[product.sku].status)
  );

const hasExtraOptions = (extra?: ProductExtraOptions): boolean =>
  Boolean(extra?.needReceipt);

// Есть ли изменения, требующие сохранения и согласования
export const hasOrderChanges = (
  products: CollectProductData[],
  marks: ProductMarks,
  extras: ProductExtras
): boolean =>
  products.some(
    (product) =>
      (marks[product.sku] && isProblemStatus(marks[product.sku].status)) ||
      hasExtraOptions(extras[product.sku])
  );

// Остаётся ли товар в заказе после решения по согласованию.
// «Нет в наличии» и «Брак» удаляются всегда, «меньше»/«срок» — по решению
export const isProductKept = (
  mark: ProductMark | undefined,
  decision: AgreementDecision | undefined
): boolean => {
  if (!mark || !isProblemStatus(mark.status)) return true;
  if (
    mark.status === CollectStatus.OUT_OF_STOCK ||
    mark.status === CollectStatus.DEFECT
  ) {
    return false;
  }
  return decision !== AgreementDecision.REMOVE;
};

export const getFinalQuantity = (
  product: CollectProductData,
  mark: ProductMark | undefined
): number =>
  mark?.status === CollectStatus.LESS && mark.quantity
    ? mark.quantity
    : product.quantity;

// Базовая цена за единицу.
// Для партнёрских заказов с наценкой (Glovo) наценка приходит отдельным
// полем partner_price (→ partnerPrice) — берём его. Для Wolt/остальных
// такого поля нет → обычная цена (price_with_warehouse_discount).
export const getBaseUnitPrice = (product: CollectProductData): number =>
  product.partnerPrice || product.price || 0;

// Эффективная цена за единицу (учитывает статус «Другая цена» и наценку партнёра)
export const getEffectiveUnitPrice = (
  product: CollectProductData,
  mark: ProductMark | undefined
): number =>
  mark?.status === CollectStatus.OTHER_PRICE && mark.newPrice
    ? mark.newPrice
    : getBaseUnitPrice(product);

// Итоговая сумма по товару с учётом изменений кол-ва/цены
export const getProductLineTotal = (
  product: CollectProductData,
  mark: ProductMark | undefined
): number =>
  getFinalQuantity(product, mark) * getEffectiveUnitPrice(product, mark);

// Изменилась ли строка товара (кол-во или цена)
export const hasLineChange = (mark: ProductMark | undefined): boolean =>
  mark?.status === CollectStatus.LESS ||
  mark?.status === CollectStatus.OTHER_PRICE;

// Итоговый состав заказа для putOrder (формат formatOrderItemsInfo)
export const buildFinalOrderItems = (
  products: CollectProductData[],
  marks: ProductMarks,
  decisions: AgreementDecisions
): CollectProductData[] =>
  products
    .filter((product) =>
      isProductKept(marks[product.sku], decisions[product.sku])
    )
    .map((product) => ({
      ...product,
      quantity: getFinalQuantity(product, marks[product.sku]),
    }));

// Актуальная сумма заказа с учётом всех отметок и решений
export const getCurrentOrderTotal = (
  products: CollectProductData[],
  marks: ProductMarks,
  decisions: AgreementDecisions
): number =>
  products.reduce((sum, product) => {
    const mark = marks[product.sku];
    if (!isProductKept(mark, decisions[product.sku])) return sum;
    return sum + getProductLineTotal(product, mark);
  }, 0);

export const getFinalOrderAmount = getCurrentOrderTotal;

// Причины по товарам для sendEdit (item-объекты в формате бэкенда).
// Поле defect новое — форма согласована предварительно, может уточниться
export const buildSendEditItems = (
  products: CollectProductData[],
  marks: ProductMarks,
  extras: ProductExtras
) =>
  products.map((product) => {
    const mark = marks[product.sku];
    const extra = extras[product.sku] || {};

    return {
      name: product.name,
      sku: product.sku,
      need_receipt: Boolean(extra.needReceipt),
      other_price: {
        is_other_price: mark?.status === CollectStatus.OTHER_PRICE,
        price:
          mark?.status === CollectStatus.OTHER_PRICE ? mark.newPrice || 0 : 0,
      },
      out_of_stock: {
        is_out_of_stock: mark?.status === CollectStatus.OUT_OF_STOCK,
        in_fact:
          mark?.status === CollectStatus.OUT_OF_STOCK &&
          mark.outOfStockReason === 'in_fact',
        in_database:
          mark?.status === CollectStatus.OUT_OF_STOCK &&
          mark.outOfStockReason === 'in_database',
      },
      packing_diff: {
        is_packing_diff: false,
        diff: '',
      },
      term: {
        is_term: mark?.status === CollectStatus.EXPIRY,
        exp_date: mark?.status === CollectStatus.EXPIRY ? mark.expDate : '',
        offer_price: 0,
      },
      defect: {
        is_defect: mark?.status === CollectStatus.DEFECT,
        reason: mark?.status === CollectStatus.DEFECT ? mark.defectReason : '',
      },
    };
  });

// Нужно ли вообще отправлять sendEdit (есть хотя бы одна причина)
export const hasSendEditReasons = (
  products: CollectProductData[],
  marks: ProductMarks,
  extras: ProductExtras
): boolean =>
  products.some((product) => {
    const mark = marks[product.sku];
    return (
      (mark &&
        [
          CollectStatus.OUT_OF_STOCK,
          CollectStatus.EXPIRY,
          CollectStatus.DEFECT,
          CollectStatus.OTHER_PRICE,
        ].includes(mark.status)) ||
      hasExtraOptions(extras[product.sku])
    );
  });

export const formatTenge = (amount: number): string =>
  `${amount.toLocaleString('ru-RU')} ₸`;

// Нормализует телефон к единому виду «+<цифры>».
// Защищает от двойного «+» (бэкенд отдаёт номер уже с «+», а маппинг
// в OrdersPage/helpers дополнительно его добавляет → «++...»)
export const formatPhone = (phone?: string): string => {
  const digits = (phone || '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};
